/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

import zIndex from '@amblique/king-living/app/theme/foundations/z-index';

const helpers = createMultiStyleConfigHelpers([
  'root', 'container', 'slide', 'paginationContainer', 'navigation', 'pagination', 'scrollbar', 'autoplay'
]);

export default helpers.defineMultiStyleConfig({
  baseStyle: ({theme, presets = [], options = {}}) => ({
    /**
     * Styles applied to swiper container
     */
    container: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      paddingBottom: options.scrollbar ? 3 : 0,
      userSelect: 'none',

      sx: {
        '&:not(.swiper-initialized)': {
          // display: 'none',
        },
      },
    },

    /**
     * Styles applied to individual slides
     */
    slide: {
      // height: 'initial',
    },

    /**
     * @link [Navigation CSS Custom Properties](https://swiperjs.com/swiper-api#navigation-css-custom-properties)
     */
    navigation: {
      backdropFilter: 'blur(15px)',
      backgroundColor: 'brandColors.greyscale20',
      borderRadius: 'full',
      cursor: 'pointer',
      display: {base: 'none', lg: 'flex'},
      height: '50px',
      margin: '0',
      transform: 'translateY(-50%)',
      transition: 'all 0.2s ease',
      width: '50px',

      _hover: {
        backgroundColor: 'brandColors.greyscale50',
      },

      _after: {
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '12px 12px',
        content: '""',
        display: 'block',
        height: '100%',
        width: '100%',
      },

      sx: {
        '&.swiper-button-next::after': {
          backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M3.87485%2012.625L9.23485%207.265C9.26968%207.23022%209.29731%207.18891%209.31617%207.14344C9.33502%207.09797%209.34473%207.04922%209.34473%207C9.34473%206.95078%209.33502%206.90203%209.31617%206.85656C9.29731%206.81109%209.26968%206.76978%209.23485%206.735L3.87485%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
        },

        '&.swiper-button-prev::after': {
          backgroundImage: 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2214%22%20viewBox%3D%220%200%2012%2014%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M8.12515%2012.625L2.76515%207.265C2.73032%207.23022%202.70269%207.18891%202.68383%207.14344C2.66498%207.09797%202.65527%207.04922%202.65527%207C2.65527%206.95078%202.66498%206.90203%202.68383%206.85656C2.70269%206.81109%202.73032%206.76978%202.76515%206.735L8.12515%201.375%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E',
        },

        '&.swiper-button-disabled': {
          cursor: 'default',
          opacity: 0.5,
          pointerEvents: 'initial',
        },
      },
    },

    /**
     * @link [Pagination CSS Custom Properties](https://swiperjs.com/swiper-api#pagination-css-custom-properties)
     */
    pagination: {
      sx: {
        '--swiper-pagination-bullet-horizontal-gap': '0',

        // Bullets Pagination
        '&.swiper-pagination-bullets': {
          backdropFilter: 'blur(15px)',
          backgroundColor: theme.colors.brandColors.greyscale20,
          borderRadius: '50px',
          bottom: '16px',
          display: 'flex',
          gap: '10px',
          left: '50%',
          padding: '8px 11px',
          transform: 'translateX(-50%)',
          width: 'auto',
        },

        '.swiper-pagination-bullet': {
          backgroundColor: theme.colors.brandColors.linen03,
          borderRadius: 'full',
          height: '4px',
          width: '4px',
        },

        '.swiper-pagination-bullet-active': {
          backgroundColor: theme.colors.brandColors.greyscale40,
        },

        // Progressbar Pagination
        '&.swiper-pagination-progressbar': {
          backgroundColor: theme.colors.brandColors.greyscale10,
          bottom: '32px',
          height: '2px',
          left: 'auto',
          textAlign: 'left',
          top: 'auto',
          width: '260px',
        },

        '.swiper-pagination-progressbar-fill': {
          backgroundColor: theme.colors.brandColors.greyscale90,
        },
      },
    },

    /**
     * @link [Scrollbar CSS Custom Properties](https://swiperjs.com/swiper-api#scrollbar-css-custom-properties)
     */
    scrollbar: {
      sx: {
        '--swiper-scrollbar-bottom': 0,
      },
    },
  }),
  variants: {
    /**
     * @param {Object} args - Component arguments
     * @param {Contentful.MediaCarousel} args.component - MediaCarousel component data
     * @param {boolean} [args.isComplete] - Whether component has all fields
     * @param {boolean} [args.isFetching] - Whether additional data is being fetched
     *
     * @returns {MediaCarouselStyles} - Component styles
     */
    'CF-010': (args) => ({
      paginationContainer: {
        alignItems: 'center',
        backdropFilter: 'blur(1px)',
        background: 'brandColors.greyscale40',
        borderRadius: {base: '2px', lg: '5px'},
        bottom: {base: '10px', lg: '32px'},
        display: 'flex',
        gap: {base: '2px', lg: '10px'},
        padding: {base: '3px 4px', lg: '8px 7px'},
        position: 'absolute',
        right: {base: '16px', lg: '50%'},
        transform: {base: 'none', lg: 'translateX(50%)'},
        zIndex: 1,
      },

      navigation: {
        backdropFilter: 'none',
        backgroundColor: 'transparent',
        display: 'flex',
        height: '24px',
        position: 'static',
        transform: 'none',
        width: '24px',

        _after: {
          backgroundSize: '10px 10px',
        },

        _hover: {
          backgroundColor: 'transparent',
        },
      },

      pagination: {
        color: 'white',
        display: 'flex',
        flexBasis: 0,
        fontSize: {base: '10px', lg: '12.472px'},
        fontWeight: '500',
        gap: {base: '3px', lg: '3.742px'},
        lineHeight: '24px',
        position: 'static',
        transform: 'none',
      },

      autoplay: {
        bottom: {base: '10px', lg: '32px'},
        left: {base: '16px', lg: '32px'},
        padding: {base: '5px', lg: '5.5px'},
        paddingX: {base: '5px', lg: '5.5px'},
        position: 'absolute',
        zIndex: 1,
      },
    }),

    'CF-011': (args) => ({
      root: {
        position: 'relative',
        overflow: 'hidden',
      },
    }),

    'CF-011-Thumbs': (args) => ({
      container: {
        bottom: {base: '50px'},
        left: {base: '45px', lg: '80px'},
        overflow: {base: 'visible', lg: 'hidden'},
        position: 'absolute',
        width: {base: 'calc(100% - 90px)', lg: 'calc(100% - 160px)'},
        zIndex: zIndex.zIndices.overlay,
      },

      slide: {
        borderTop: '1px solid',
        borderTopColor: 'whiteAlpha.400',
        color: 'whiteAlpha.400',
        cursor: 'pointer',
        fontSize: {base: '18px', lg: '24px'},
        fontWeight: '500',
        paddingTop: '28px',

        sx: {
          '&.swiper-slide-thumb-active': {
            borderTopColor: 'white',
            borderTopWidth: '3px',
            color: 'white',
          },

          '.amb-markup': {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            lineHeight: 'shorter',
          },

          '.amb-markup p': {
            margin: 0,
          },

          '.amb-markup p + .amb-markup p': {
            letterSpacing: '-0.24px',
          },
        },
      },
    }),

    'CF-012': (args) => ({
      navigation: {
        backdropFilter: 'none',
        backgroundColor: 'transparent',
        bottom: {base: '30px', lg: '70px'},
        top: 'auto',

        _after: {
          backgroundSize: '20px 20px',
        },

        _hover: {
          backgroundColor: 'transparent',
        },

        sx: {
          '&.swiper-button-next': {
            left: '50%',
          },
          '&.swiper-button-prev': {
            left: 'auto',
            right: '50%',
          },
        },
      },
      root: {
        position: 'relative',
      },
    }),

    'CF-012-Thumbs': (args) => ({
      container: {
        left: '50%',
        position: 'absolute',
        top: {base: '220px', lg: '310px'},
        transform: 'translateX(-50%)',
        width: 'calc(100% - 348px)',
        zIndex: zIndex.zIndices.overlay,
      },

      slide: {
        borderTop: '1px solid',
        borderTopColor: 'whiteAlpha.400',
        color: 'whiteAlpha.400',
        cursor: 'pointer',
        fontSize: {base: '13px', lg: '14px'},
        fontWeight: '400',
        lineHeight: 'shorter',
        paddingTop: '12px',
        width: '160px',

        sx: {
          '&.swiper-slide-thumb-active': {
            borderTopColor: 'white',
            borderTopWidth: '3px',
            color: 'white',
            fontWeight: '700',
          },

          '.amb-markup': {
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5em',
            lineHeight: 'shorter',
          },

          '.amb-markup p': {
            margin: 0,
          },
        },
      },

      navigation: {
        sx: {
          '&.swiper-button-disabled': {
            display: 'none',
          },

          '&.swiper-button-next': {
            right: '0',
          },
          '&.swiper-button-prev': {
            left: '0',
          },
        },
      },
    }),

    'CF-013': (args) => ({
      root: {
        backgroundColor: 'white',
        margin: 0,
        paddingBottom: {base: '107px', lg: '200px'},
        position: 'relative',
      },
    }),

    'CF-013-Thumbs': (args) => ({
      container: {
        bottom: {base: '48px', lg: '80px'},
        left: '50%',
        position: 'absolute',
        transform: 'translateX(-50%)',
        width: '100%',
      },
      slide: {
        cursor: 'pointer',
        maxHeight: {base: '43px', lg: '62px'},
        opacity: 0.5,
        width: {base: '50px', lg: '60px'},

        sx: {
          '&.swiper-slide-thumb-active': {
            opacity: 1,
          },
        },
      },
    }),

    'CF-022': (args) => ({
      root: {
        overflow: 'hidden',
      },
      container: {
        overflow: 'visible',
        marginX: 'auto',
        maxWidth: 'container.xxxl',
        paddingLeft: [4, 4, 6, 8],
        paddingRight: [4, 4, 6, 8],
      },
      slide: {
        height: {base: '266px', lg: '344px'},
        paddingBottom: {base: '56px', lg: '120px'},
        width: {base: '217px', lg: '280px'},
      },
    }),

    'CF-046': (args) => ({
      root: {
        overflow: 'hidden',
      },
      container: {
        marginX: 'auto',
        maxWidth: 'container.xxxl',
        overflow: 'visible',
        paddingLeft: [4, 4, 6, 8],
        paddingRight: [4, 4, 6, 8],
      },
      slide: {
        marginBottom: {base: '56px', lg: '120px'},
        width: {base: '280px', lg: '667px'},
      },
      navigation: {
        sx: {
          '&.swiper-button-disabled': {
            opacity: 1,
          },
        },
      },
    }),
  },
});
